/**
 * Client-side permission evaluation.
 *
 * This MIRRORS the server's `authorize()` middleware — it does not replace it.
 * SRS FR-35: "The system shall enforce server-side permission checks on every
 * API request, independent of UI restrictions." This module exists only to hide
 * controls the user cannot use; the API remains the authority, and every guarded
 * action is re-checked there.
 *
 * The three rules below are the same ones the server applies:
 *   `*`                → super-admin wildcard, grants everything
 *   `resource:manage`  → grants every action on that resource
 *   `resource:action`  → exact grant
 */

export const WILDCARD = '*';
export const MANAGE = 'manage';

export type PermissionSet = ReadonlySet<string>;

export function toPermissionSet(permissions: readonly string[] | undefined): PermissionSet {
  return new Set(permissions ?? []);
}

/**
 * Does this permission set satisfy `required` (e.g. "project:create")?
 */
export function can(permissions: PermissionSet, required: string): boolean {
  if (permissions.has(WILDCARD)) return true;
  if (permissions.has(required)) return true;

  const separator = required.indexOf(':');
  if (separator === -1) return false;

  const resource = required.slice(0, separator);
  return permissions.has(`${resource}:${MANAGE}`);
}

/** True if EVERY requirement is satisfied. */
export function canAll(permissions: PermissionSet, required: readonly string[]): boolean {
  return required.every((r) => can(permissions, r));
}

/** True if ANY requirement is satisfied. */
export function canAny(permissions: PermissionSet, required: readonly string[]): boolean {
  return required.some((r) => can(permissions, r));
}

/**
 * Roles whose read grants on shared resources (projects, tasks, the
 * employee directory, …) are deliberately company-wide — mirrors each
 * API module's identical `BROAD_*_ROLES` set (see e.g.
 * project.service.js). A login holding none of these is on the narrow
 * "Employee" self-service tier: the API already scopes their data down to
 * their own, so the dashboard renders the matching narrow layout instead
 * of KPI cards it can't back up. Checked by role rather than derived from
 * a permission, for the same reason the server can't either — a shared
 * permission key can't tell "granted for oversight" apart from "granted
 * for self-service" on its own.
 */
const BROAD_STAFF_ROLES = new Set([
  'super-admin', 'tenant-owner', 'admin', 'hr', 'finance', 'manager', 'team-lead', 'member', 'viewer',
]);

/** True for a login on the narrow Employee self-service tier (no broader
 *  oversight role held) — a layout decision only, never a security boundary. */
export function isEmployeeTier(roles: readonly string[] | undefined): boolean {
  if (!roles?.length) return false;
  if (roles.includes('client') || roles.includes('vendor')) return false;
  return !roles.some((r) => BROAD_STAFF_ROLES.has(r));
}
